import React, { ChangeEvent, useMemo } from "react";
import { IRpgCharacter } from "../../../models/IRpgCharacter";
import { CharacterPortrait } from "../../../components/Character/CharacterImage/CharacterPortrait";
import type { ICharacterPortraitProps } from "../../../models/ICharacter";
import { getCharacterPortrait } from "../characterHelpers";
import { IRpgAlignment } from "../../../models/IRpgAlignment";

type GenderOption = "Male" | "Female";
const GENDER_OPTIONS: GenderOption[] = ["Male", "Female"];
type ColorSwatch = {
    name: string;
    hex: string;
};
const EYE_SWATCHES: ColorSwatch[] = [
    { name: "Brown", hex: "#6b4a2b" },
    { name: "Hazel", hex: "#7a6a2f" },
    { name: "Green", hex: "#3f7d4a" },
    { name: "Blue", hex: "#3a6ea5" },
    { name: "Gray", hex: "#7a8792" },
    { name: "Amber", hex: "#b07a22" },
];
const HAIR_SWATCHES: ColorSwatch[] = [
    { name: "Black", hex: "#1c1c1c" },
    { name: "Dark Brown", hex: "#3b2718" },
    { name: "Brown", hex: "#5a3a22" },
    { name: "Auburn", hex: "#7b2f1b" },
    { name: "Blonde", hex: "#c2a55a" },
    { name: "Gray", hex: "#8e8e8e" },
    { name: "White", hex: "#e6e6e6" },
];

interface IAppearancePanelProps {
    isReadOnly: boolean;
    tempCharacter: IRpgCharacter;
    setTempCharacter: React.Dispatch<React.SetStateAction<IRpgCharacter>>;
    alignmentList: IRpgAlignment[];
}

const AppearancePanel: React.FC<IAppearancePanelProps> = ({ isReadOnly, tempCharacter, setTempCharacter, alignmentList }) => {
    const portraitProps: ICharacterPortraitProps = useMemo(() => {
        return {
            layout: "standard",
            size: "lg",
            imageUrl: getCharacterPortrait(tempCharacter),
            level: 1,
            name: tempCharacter.Name,
            subtitle: `${tempCharacter.Race} ${tempCharacter.Class}`.trim(),
        };
    }, [tempCharacter]);

    const heightValue = tempCharacter.Appearance?.Height ?? 48;
    const weightValue = tempCharacter.Appearance?.Weight ?? 80;
    const selectedEyeColor = tempCharacter.Appearance?.EyeColor ?? "";
    const selectedHairColor = tempCharacter.Appearance?.HairColor ?? "";

    const selectedAlignment: IRpgAlignment | undefined = useMemo(() => {
        const selectedKey = (tempCharacter.Alignment ?? "").trim();
        if (selectedKey.length === 0) {
            return undefined;
        }

        return alignmentList.find((a) => a.Alignment === selectedKey);
    }, [alignmentList, tempCharacter.Alignment]);

    const tooltipLine1 = selectedAlignment ? `${selectedAlignment.Examples}` : "Select an Alignment to see Examples and Descriptions.";
    const tooltipLine2 = selectedAlignment ? `${truncateWithEllipsis(selectedAlignment.Description, 1200)}` : "";

    // Helpers
    function clampNumber(value: number, min: number, max: number): number {
        return Math.min(max, Math.max(min, value));
    }

    const displayTextHeight = (height: number): string => {
        const feet = Math.floor(height / 12);
        const inches = height % 12;
        return `${feet}' ${inches}"`;
    }

    function safeInt(value: string): number | undefined {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            return undefined;
        }

        const parsed = Number.parseInt(trimmed, 10);
        if (Number.isNaN(parsed)) {
            return undefined;
        }

        return parsed;
    }

    // TODO: These 2 should probably be part of a utility helper class
    function normalizeLineBreaks(text: string): string
    {
        const tmp = (text ?? "");
        // Turn literal "\r\n" and "\n" into actual newlines
        return tmp
            .replaceAll("\\r\\n", "\n")
            .replaceAll("\\n", "\n")
            .replaceAll("\\r", "\n");
    }

    function truncateWithEllipsis(text: string, maxChars: number): string
    {
        const tmp = (normalizeLineBreaks(text) ?? "").trim();
        if (tmp.length <= maxChars) {
            return tmp;
        }

        return `${tmp.substring(0, maxChars).trimEnd()}...`;
    }

    // Event Handlers
    function onAge_Update(raw: string): void {
        if (isReadOnly) {
            return;
        }

        const parsed = safeInt(raw);

        setTempCharacter((prev) => ({
            ...prev,
            Appearance: {
                ...prev.Appearance,
                Age: parsed,
            },
        }));
    }

    const onAlignment_Update = (event: ChangeEvent<HTMLSelectElement>) => {
        if (isReadOnly) {
            return;
        }

        const val = event.target.value;

        setTempCharacter((prev) => ({
            ...prev, Alignment: val
        }));
    };

    function onCharacterName_Update(name: string): void {
        if (isReadOnly) {
            return;
        }

        setTempCharacter((prev) => ({
            ...prev,
            Name: name,
        }));
    }

    function onGender_Update(gender: GenderOption): void {
        if (isReadOnly) {
            return;
        }

        setTempCharacter((prev) => ({
            ...prev,
            Appearance: {
                ...prev.Appearance,
                Gender: gender,
            },
        }));
    }

    function onHeight_Update(raw: string): void {
        if (isReadOnly) {
            return;
        }

        const parsed = safeInt(raw);
        const nextHeight = parsed === undefined ? undefined : clampNumber(parsed, 48, 84);

        setTempCharacter((prev) => ({
            ...prev,
            Appearance: {
                ...prev.Appearance,
                Height: nextHeight,
            },
        }));
    }

    function onWeight_Update(raw: string): void {
        if (isReadOnly) {
            return;
        }

        const parsed = safeInt(raw);
        const nextWeight = parsed === undefined ? undefined : clampNumber(parsed, 80, 400);

        setTempCharacter((prev) => ({
            ...prev,
            Appearance: {
                ...prev.Appearance,
                Weight: nextWeight,
            },
        }));
    }

    function onEyeColor_Update(colorName: string): void {
        if (isReadOnly) {
            return;
        }

        setTempCharacter((prev) => ({
            ...prev,
            Appearance: {
                ...prev.Appearance,
                EyeColor: colorName,
            },
        }));
    }

    function onHairColor_Update(colorName: string): void {
        if (isReadOnly) {
            return;
        }

        setTempCharacter((prev) => ({
            ...prev,
            Appearance: {
                ...prev.Appearance,
                HairColor: colorName,
            },
        }));
    }

    return (
        <div className="appearance">
            <div className="appearance__form">
                {/* Name and Alignment */}
                <div className="appearance__row appearance__row--nameAlign">
                    {/* Name */}
                    <div className="appearance__field">
                        <label className="appearance__label">Character Name</label>
                        <input
                            className="appearance__input"
                            type="text"
                            value={tempCharacter.Name ?? ""}
                            disabled={isReadOnly}
                            onChange={(evt) => onCharacterName_Update(evt.target.value)}
                        />
                    </div>

                    {/* Alignment */}
                    <div className="appearance__field">
                        <label className="appearance__label">Alignment</label>

                        <div className="appearance__selectWithInfo">
                            {/* <Select name='alignment' options={alignmentList} placeholder="Alignment" onChange={onAlignment_Update} /> */}
                            <select
                                id="alignment"
                                name="alignment"
                                className="appearance__input appearance__select"
                                value={tempCharacter.Alignment ?? ""}
                                disabled={isReadOnly}
                                onChange={onAlignment_Update}
                            >
                                <option value="">-- Select an Alignment --</option>
                                {alignmentList.map((alignemt) => (
                                    <option key={alignemt.Id} value={alignemt.Alignment}>
                                        {alignemt.Alignment}
                                    </option>
                                ))}
                            </select>

                            {/* info icon */}
                            <button
                                type="button"
                                className="appearance__infoBtn"
                                aria-label="Alignment info"
                            >
                                i
                                <span className="appearance__tooltip" role="tooltip">
                                    <div className="appearance__tooltipLine">
                                        <span className="appearance__tooltipLabel">Example:</span>
                                        <span className="appearance__tooltipValue">{tooltipLine1}</span>
                                    </div>
                                    {tooltipLine2.length > 0 ? (
                                        <div className="appearance__tooltipLine">
                                            <span className="appearance__tooltipLabel">Description:</span>
                                            <span className="appearance__tooltipValue">{tooltipLine2}</span>
                                        </div>
                                    ) : null}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Gender and Age */}
                <div className="appearance__row appearance__row--genderAge">
                    {/* Gender */}
                    <div className="appearance__field appearance__field--gender">
                        <label className="appearance__label appearance__label--gender">Gender</label>

                        <div className="appearance__radioGroup">
                            {GENDER_OPTIONS.map((gender) => {
                                const isChecked = (tempCharacter.Appearance?.Gender ?? "") === gender;

                                return (
                                    <label key={gender} className="appearance__radioOption">
                                        <input
                                            type="radio"
                                            name="appearance-gender"
                                            checked={isChecked}
                                            disabled={isReadOnly}
                                            onChange={() => onGender_Update(gender)}
                                        />
                                        <span>{gender}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* Age */}
                    <div className="appearance__field appearance__field--age">
                        <label className="appearance__label">Age</label>
                        <input
                            className="appearance__input appearance__input--age"
                            type="text"
                            inputMode="numeric"
                            value={tempCharacter.Appearance?.Age === undefined ? "" : String(tempCharacter.Appearance?.Age)}
                            disabled={isReadOnly}
                            onChange={(evt) => onAge_Update(evt.target.value)}
                        />
                    </div>
                </div>

                {/* Height */}
                <div className="appearance__field">
                    <label className="appearance__label">Height</label>
                    <div className="appearance__sliderRow">
                        <input
                            className="appearance__slider"
                            type="range"
                            min={48}
                            max={84}
                            value={heightValue}
                            disabled={isReadOnly}
                            onChange={(evt) => onHeight_Update(evt.target.value)}
                        />
                        <div className="appearance__valueBox">{displayTextHeight(heightValue)}</div>
                    </div>
                </div>

                {/* Weight */}
                <div className="appearance__field">
                    <label className="appearance__label">Weight</label>
                    <div className="appearance__sliderRow">
                        <input
                            className="appearance__slider"
                            type="range"
                            min={80}
                            max={400}
                            value={weightValue}
                            disabled={isReadOnly}
                            onChange={(evt) => onWeight_Update(evt.target.value)}
                        />
                        <div className="appearance__valueBox">{weightValue}</div>
                    </div>
                </div>

                {/* Eye Color */}
                <div className="appearance__field">
                    <label className="appearance__label">Eye Color</label>
                    <div className="appearance__swatches">
                        {EYE_SWATCHES.map((swatch) => {
                            const isSelected = selectedEyeColor === swatch.name;

                            return (
                                <button
                                    key={swatch.name}
                                    type="button"
                                    className={`appearance__swatch${isSelected ? " is-selected" : ""}`}
                                    title={swatch.name}
                                    disabled={isReadOnly}
                                    onClick={() => onEyeColor_Update(swatch.name)}
                                >
                                    <span className="appearance__swatchColor" style={{ backgroundColor: swatch.hex }} />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Hair Color */}
                <div className="appearance__field">
                    <label className="appearance__label">Hair Color</label>
                    <div className="appearance__swatches">
                        {HAIR_SWATCHES.map((swatch) => {
                            const isSelected = selectedHairColor === swatch.name;

                            return (
                                <button
                                    key={swatch.name}
                                    type="button"
                                    className={`appearance__swatch${isSelected ? " is-selected" : ""}`}
                                    title={swatch.name}
                                    disabled={isReadOnly}
                                    onClick={() => onHairColor_Update(swatch.name)}
                                >
                                    <span className="appearance__swatchColor" style={{ backgroundColor: swatch.hex }} />
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="appearance__portrait">
                <CharacterPortrait {...portraitProps} />
            </div>
        </div>
    );
};

export default AppearancePanel;
